/**
 * Storage-agent protocol (version 1).
 *
 * Agents run next to on-site storage and only ever connect OUTBOUND to the
 * control plane over HTTPS: enrol once with a short-lived token, then
 * authenticate with their own credential, heartbeat with capabilities, report
 * discovered files, and lease jobs by long-polling. Every call is idempotent
 * so an agent can resume after a network drop or restart.
 */
import { z } from 'zod';
import { LOG_LEVELS } from './log-levels.ts';
import { DerivativeJobSchema, DerivativeResultSchema, ProbeJobSchema, ProbeResultSchema } from './jobs.ts';
import { QcJobSchema, QcResultSchema } from './qc.ts';
import { GATEWAY_LIMITS, GATEWAY_METHODS, GatewayCapabilitySchema, HttpResultSchema } from './gateway.ts';

export const AGENT_PROTOCOL_VERSION = 1;

/** How an agent is installed: the single-file program, a container, or run from the source tree. */
export const AGENT_PACKAGINGS = ['binary', 'docker', 'source'] as const;
export type AgentPackaging = (typeof AGENT_PACKAGINGS)[number];

export const EnrollRequest = z.object({
  protocol: z.literal(AGENT_PROTOCOL_VERSION),
  token: z.string().min(24),
  hostname: z.string().max(255),
  version: z.string().max(64),
});

export const EnrollResponse = z.object({
  agentId: z.string().uuid(),
  credential: z.string(),
  name: z.string(),
  site: z.string(),
  roots: z.array(z.object({ name: z.string(), locationId: z.string().uuid() })),
});
export type EnrollResponse = z.infer<typeof EnrollResponse>;

export const RootStatus = z.object({
  name: z.string(),
  readable: z.boolean(),
  /** Whether the agent can write here (needed for PRISM-managed roots). Older agents omit it. */
  writable: z.boolean().optional(),
  freeBytes: z.number().nullable(),
  totalBytes: z.number().nullable(),
  files: z.number().int().nullable(),
  error: z.string().optional(),
});

/**
 * The agent's base folders. Storage folders are chosen in PRISM under the storage
 * root, watch folders under the watch root, so a storage folder can never be watched
 * and ingested from. Export folders go under the export root when the agent has one
 * (0.27.0), else under the storage root as before. Edit folders (1.0.0-rc.6) go
 * under the edit root, which must be separate from the other three.
 */
export const AGENT_BASES = ['storage', 'watch', 'export', 'edit'] as const;
export const BaseStatus = z.object({
  configured: z.boolean(),
  readable: z.boolean(),
  writable: z.boolean(),
  freeBytes: z.number().nullable(),
  totalBytes: z.number().nullable(),
  error: z.string().optional(),
});
/** Status of one folder PRISM assigned to the agent, by its storage location. */
export const FolderStatus = z.object({
  locationId: z.string().uuid(),
  readable: z.boolean(),
  writable: z.boolean(),
  freeBytes: z.number().nullable(),
  totalBytes: z.number().nullable(),
  files: z.number().int().nullable().default(null),
  error: z.string().optional(),
});

export const HeartbeatRequest = z.object({
  protocol: z.literal(AGENT_PROTOCOL_VERSION),
  hostname: z.string().max(255),
  version: z.string().max(64),
  capabilities: z.object({
    operations: z.array(z.string()),
    ffmpeg: z.string().nullable(),
    /** Named roots (PRISM_AGENT_ROOTS, earlier releases). */
    roots: z.array(RootStatus).default([]),
    bases: z.object({ storage: BaseStatus.optional(), watch: BaseStatus.optional(), export: BaseStatus.optional(), edit: BaseStatus.optional() }).default({}),
    folders: z.array(FolderStatus).default([]),
    /** Transfer lane concurrency (PRISM_AGENT_CONCURRENCY). */
    maxConcurrency: z.number().int().positive(),
    /** Media (probe and derive) lane concurrency; absent from agents before 0.19. */
    maxTranscodeConcurrency: z.number().int().positive().optional(),
    /** Hardware video encoders the agent found; reported from a later step. */
    hwEncoders: z.array(z.string()).optional(),
    /** How the agent is installed: the single-file program, a container, or from source; absent from agents before 0.20. */
    packaging: z.enum(AGENT_PACKAGINGS).optional(),
    /** Operating system and CPU architecture (Node's process.platform and process.arch). */
    os: z.string().max(32).optional(),
    arch: z.string().max(32).optional(),
    /** The agent limits a fetch or upload to its spec's maxBytesPerSecond (1.1.0); absent from older agents. */
    throttle: z.boolean().optional(),
    /**
     * The API gateway (1.6.0, gateway.ts): present when PRISM_AGENT_GATEWAY=on, with the services of a valid
     * gateway.json or the file's error. `operations` lists 'gateway' only with a valid file. Absent from older agents.
     */
    gateway: GatewayCapabilitySchema.optional(),
  }),
  activeJobs: z.array(z.string()).default([]),
  /** The level the agent logs at now; absent from agents before 0.23. */
  logLevel: z.string().max(10).optional(),
  /**
   * The agent reaches PRISM over plain http to a non-loopback host (1.8.1; PRISM_AGENT_ALLOW_HTTP). Absent from older
   * agents, read as false.
   */
  insecureControl: z.boolean().default(false),
});

/**
 * What PRISM does with an agent root:
 *  - in_place: scan and catalogue files where they are (never moved or deleted)
 *  - managed:  PRISM-managed on-site storage; a storage tier PRISM writes to and deletes from
 *  - watch:    an ingest watch folder; files are imported and moved or copied away
 *  - idle:     nothing
 */
export const AGENT_ROOT_MODES = ['in_place', 'managed', 'watch', 'idle'] as const;
export type AgentRootMode = (typeof AGENT_ROOT_MODES)[number];

/** How PRISM uses a folder on an agent: managed storage, an archive catalogued in place, an export destination, a watch folder, or an edit location (1.0.0-rc.6). */
export const AGENT_FOLDER_MODES = ['managed', 'in_place', 'export', 'watch', 'edit'] as const;
export type AgentFolderMode = (typeof AGENT_FOLDER_MODES)[number];

export const HeartbeatResponse = z.object({
  roots: z.array(z.object({ name: z.string(), locationId: z.string().uuid(), mode: z.enum(AGENT_ROOT_MODES).default('in_place') })),
  /** Folders PRISM assigned to this agent, relative to one of its base folders. */
  folders: z.array(z.object({
    locationId: z.string().uuid(), base: z.enum(AGENT_BASES), path: z.string(), mode: z.enum(AGENT_FOLDER_MODES),
  })).default([]),
  /** Watch folders this agent scans at its site: a folder (by location) or a named root. */
  watchFolders: z.array(z.object({
    id: z.string().uuid(), root: z.string().nullable().default(null), locationId: z.string().uuid().nullable().default(null),
    stabilitySeconds: z.number().int().positive(), includeSubfolders: z.boolean(),
  })).default([]),
  scanIntervalSeconds: z.number().int().positive(),
  stabilitySeconds: z.number().int().positive(),
  heartbeatSeconds: z.number().int().positive(),
  /** Log level chosen for this agent in System status; null: its own default. Absent from an API before 0.23, ignored by agents before 0.23. */
  logLevel: z.enum(LOG_LEVELS).nullable().optional(),
  /**
   * Jobs the agent listed as active that the workflow abandoned meanwhile (1.0.0-rc.14, #5d): the agent stops their
   * transfers and reports them failed. Absent from an API before rc.14; ignored by agents before rc.14.
   */
  cancelledJobs: z.array(z.string()).optional(),
});
export type HeartbeatResponse = z.infer<typeof HeartbeatResponse>;

/** Files are only reported once their size and modification time have been stable. */
export const DiscoveredFile = z.object({
  key: z.string().min(1).max(1024),
  sizeBytes: z.number().int().nonnegative(),
  modifiedAt: z.string(),
  checksumSha256: z.string().regex(/^[0-9a-f]{64}$/),
});

export const DiscoveryReport = z.object({
  /** A named root, or for folders assigned in PRISM the folder's location id. */
  root: z.string(),
  locationId: z.string().uuid().optional(),
  files: z.array(DiscoveredFile).max(500),
  /** Keys previously reported that are no longer present. */
  missing: z.array(z.string()).max(5000).default([]),
});
export type DiscoveryReport = z.infer<typeof DiscoveryReport>;

export const AgentProbeJob = z.object({ kind: z.literal('probe'), job: ProbeJobSchema });
export const AgentDeriveJob = z.object({ kind: z.literal('derive'), job: DerivativeJobSchema });
export const AgentUploadJob = z.object({
  kind: z.literal('upload'),
  /** Set for explicit transfers; absent when a watch folder uploads its import. */
  transferId: z.string().uuid().optional(),
  source: z.object({ locationId: z.string().uuid(), key: z.string() }),
  destination: z.object({ locationId: z.string().uuid(), key: z.string() }),
  expectedChecksumSha256: z.string().nullable(),
  /** Bandwidth limit for this copy (1.1.0, a storage migration's limit); agents before 1.1.0 ignore it. */
  maxBytesPerSecond: z.number().int().positive().optional(),
});
/** Pull bytes from the control plane (GET the job's input URL) into a PRISM-managed root or an export folder. */
export const AgentFetchJob = z.object({
  kind: z.literal('fetch'),
  transferId: z.string().uuid().optional(),
  /** Delivering an export: the rendered file ('main') or one of its sidecars (by index). */
  exportId: z.string().uuid().optional(),
  exportFile: z.union([z.literal('main'), z.number().int().nonnegative()]).optional(),
  /**
   * Send to edit (1.0.0-rc.6): one file of a hand-off, by its path in the hand-off folder; the destination key is that
   * folder and path inside the agent's edit location. Agents before rc.6 fetch it like any job input.
   */
  handoffId: z.string().uuid().optional(),
  handoffFile: z.string().min(1).max(1000).optional(),
  destination: z.object({ locationId: z.string().uuid(), key: z.string() }),
  expectedChecksumSha256: z.string().nullable(),
  /** Bandwidth limit for this copy (1.1.0, a storage migration's limit); agents before 1.1.0 ignore it. */
  maxBytesPerSecond: z.number().int().positive().optional(),
});
/** Move (or copy) a file between the agent's own roots, verified by checksum. */
export const AgentMoveJob = z.object({
  kind: z.literal('move'),
  source: z.object({ locationId: z.string().uuid(), key: z.string() }),
  destination: z.object({ locationId: z.string().uuid(), key: z.string() }),
  keepSource: z.boolean().default(false),
  expectedChecksumSha256: z.string().nullable(),
});
/** Delete files PRISM manages (or has imported) on one of the agent's roots. */
export const AgentDeleteJob = z.object({
  kind: z.literal('delete'),
  locationId: z.string().uuid(),
  keys: z.array(z.string().min(1)).min(1).max(1000),
  /**
   * What PRISM saw at each key (1.0.0-rc.14, #3): a watch-folder cleanup deletes only that file. The agent looks at the
   * key first (lstat, links not followed) and skips it, reporting why, when it is not a plain file, its size differs,
   * its modification time differs (when given), or, with no time given, its SHA-256 differs. Keys without an entry are
   * deleted by name, as before. Agents before rc.14 ignore it and delete by name.
   */
  expected: z.array(z.object({
    key: z.string().min(1),
    sizeBytes: z.number().int().nonnegative(),
    modifiedAt: z.string().optional(),
    checksumSha256: z.string().regex(/^[0-9a-f]{64}$/).optional(),
  })).max(1000).optional(),
});
/** List (and optionally create) a folder under one of the agent's base folders, for the folder picker in PRISM. */
export const AgentBrowseJob = z.object({
  kind: z.literal('browse'),
  base: z.enum(AGENT_BASES),
  path: z.string().max(1000),
  create: z.boolean().default(false),
  /**
   * Check in (1.0.0-rc.7): also list the files under the folder (four levels, 1,000 files at most) with their sizes,
   * for choosing what to check in from a hand-off folder. Agents before rc.7 ignore it and list none.
   */
  files: z.boolean().optional(),
  /**
   * Fixes after rc.9 testing: how many levels of files to list (1: the folder's own files), for checking that a file
   * elsewhere under an edit location is there. Default 4; agents before ignore it and list four.
   */
  depth: z.number().int().min(1).max(4).optional(),
});
/** Automated QC (1.0.0-rc.8) of a file on one of the agent's roots: the decode pass and packet scan, next to the media. Only sent to agents that list 'qc'. */
export const AgentQcJob = z.object({ kind: z.literal('qc'), job: QcJobSchema });
/**
 * Statistics (1.0.0-rc.13 second round): the files and bytes in one of the agent's export folders (that folder only,
 * never its base or anything beside it), at most `maxFiles` files. Only sent to agents that list 'measure'.
 */
export const AgentMeasureJob = z.object({ kind: z.literal('measure'), locationId: z.string().uuid(), maxFiles: z.number().int().positive().max(10_000_000) });
export const MeasureResult = z.object({ bytes: z.number().nonnegative(), files: z.number().int().nonnegative(), complete: z.boolean() });
/**
 * A call to a site service through the agent's API gateway (1.6.0, gateway.ts; Call site service and the Webhooks
 * page's Test). The agent checks it again against its gateway.json (service, method, the normalised path, the header
 * names), adds the service's secret headers, follows no redirects and keeps to the service's timeout and response
 * cap. Only sent to agents that list 'gateway' in their operations, and only for services they report.
 */
export const AgentHttpJob = z.object({
  kind: z.literal('http'),
  service: z.string().regex(/^[a-z][a-z0-9-]{0,39}$/),
  method: z.enum(GATEWAY_METHODS),
  /** The path below the service's base URL, as written (the agent normalises it: normaliseServicePath). */
  path: z.string().min(1).max(GATEWAY_LIMITS.pathLength),
  query: z.record(z.string().max(200), z.union([z.string().max(4000), z.array(z.string().max(4000)).max(50)])).optional(),
  /** Non-secret request headers, by names the service allows (allowRequestHeaders). */
  headers: z.record(z.string().max(100), z.string().max(4000)).optional(),
  /** A JSON or text body (≤ 1 MB in UTF-8); none for GET and HEAD. */
  body: z.discriminatedUnion('type', [
    z.object({ type: z.literal('json'), value: z.unknown() }),
    z.object({ type: z.literal('text'), value: z.string(), contentType: z.string().max(200).optional() }),
  ]).optional(),
  /** The run (or the Test) the call belongs to, for the agent's log. */
  correlationId: z.string().max(200),
}).superRefine((j, ctx) => {
  if (j.body && (j.method === 'GET' || j.method === 'HEAD')) ctx.addIssue({ code: 'custom', path: ['body'], message: `${j.method} has no body` });
  if (j.body) {
    const text = j.body.type === 'json' ? JSON.stringify(j.body.value ?? null) : j.body.value;
    if (new TextEncoder().encode(text ?? '').length > GATEWAY_LIMITS.maxRequestBytes) ctx.addIssue({ code: 'custom', path: ['body'], message: 'The body is larger than 1 MB' });
  }
  if (Object.keys(j.headers ?? {}).length > GATEWAY_LIMITS.requestHeaders) ctx.addIssue({ code: 'custom', path: ['headers'], message: `At most ${GATEWAY_LIMITS.requestHeaders} headers` });
});
export type AgentHttpJob = z.infer<typeof AgentHttpJob>;
/** New kinds go last (1.6.0: http). */
export const AgentJobSpec = z.discriminatedUnion('kind', [AgentProbeJob, AgentDeriveJob, AgentUploadJob, AgentFetchJob, AgentMoveJob, AgentDeleteJob, AgentBrowseJob, AgentQcJob, AgentMeasureJob, AgentHttpJob]);
/** Jobs served by the agent's control loop, which never waits behind long transfers. */
export const AGENT_CONTROL_JOB_KINDS = ['browse'] as const;
/** Jobs served by the agent's media lane (FFmpeg work); everything else that is not control or gateway is the transfer lane. */
export const AGENT_MEDIA_JOB_KINDS = ['probe', 'derive', 'qc'] as const;
/** Jobs served by the agent's gateway lane (1.6.0): its own concurrency and a 200 ms re-check; agents without the lane never lease them. */
export const AGENT_GATEWAY_JOB_KINDS = ['http'] as const;
export const AGENT_LANES = ['media', 'transfer', 'gateway'] as const;
/** The lane a job kind runs in on the agent; null for the control loop's. */
export function agentLaneOf(kind: string): AgentLane | null {
  if ((AGENT_CONTROL_JOB_KINDS as readonly string[]).includes(kind)) return null;
  if ((AGENT_MEDIA_JOB_KINDS as readonly string[]).includes(kind)) return 'media';
  if ((AGENT_GATEWAY_JOB_KINDS as readonly string[]).includes(kind)) return 'gateway';
  return 'transfer';
}
export type AgentLane = (typeof AGENT_LANES)[number];
export type AgentJobSpec = z.infer<typeof AgentJobSpec>;

export const LeasedJob = z.object({
  id: z.string(),
  spec: AgentJobSpec,
  leaseSeconds: z.number().int(),
  /** Where derivative/upload bytes are sent: PUT with the job's destination key. */
  outputUrl: z.string(),
  /** This lease (0.30.0): sent back with the completion, so an attempt whose lease expired cannot complete the job. */
  leaseToken: z.string().optional(),
  /** Which attempt this lease is (1 for the first). */
  attempt: z.number().int().optional(),
});
export type LeasedJob = z.infer<typeof LeasedJob>;

export const UploadResult = z.object({ sizeBytes: z.number().int(), checksumSha256: z.string() });
export const DeleteResult = z.object({
  deleted: z.number().int(),
  missing: z.number().int(),
  /** Keys left in place because the file there is not the one PRISM saw (1.0.0-rc.14); absent from older agents. */
  skipped: z.array(z.object({ key: z.string(), reason: z.string() })).optional(),
});
export const BrowseResult = z.object({
  available: z.boolean(),
  path: z.string(),
  folders: z.array(z.object({ name: z.string(), path: z.string() })),
  files: z.number().int(),
  writable: z.boolean(),
  capacity: z.object({ freeBytes: z.number(), totalBytes: z.number() }).nullable(),
  error: z.string().optional(),
  /** With `files` (1.0.0-rc.7): the files under the folder, by their path in it. */
  entries: z.array(z.object({ path: z.string(), sizeBytes: z.number(), modifiedAt: z.string() })).optional(),
  /** The file listing stopped at its limit. */
  truncated: z.boolean().optional(),
});
export type BrowseResult = z.infer<typeof BrowseResult>;

/** The lease token the agent received with the job (0.30.0); agents before 0.30.0 send none. */
const LeaseToken = z.string().min(1).max(100).optional();
/**
 * What a succeeded job of each kind answers: probe, derive, upload (and fetch and move, the same shape), delete, browse,
 * qc, measure, http (1.6.0). Zod takes the first member that matches, so new members go last. `null` is a derive with nothing to make
 * (a rendition of a still image, a waveform without audio), which the workflow reads as "not applicable".
 * Fixed after rc.14: measure (and that null) were missing, so the agent's answer was refused and the job leased again.
 */
export const JobCompletion = z.union([
  // 1.6.0: an http job's HttpResult, last.
  z.object({ status: z.literal('succeeded'), result: z.union([ProbeResultSchema, DerivativeResultSchema, UploadResult, DeleteResult, BrowseResult, QcResultSchema, MeasureResult, z.null(), HttpResultSchema]), leaseToken: LeaseToken }),
  z.object({
    status: z.literal('failed'),
    error: z.object({ code: z.string(), message: z.string(), retryable: z.boolean() }),
    leaseToken: LeaseToken,
  }),
]);
export type JobCompletion = z.infer<typeof JobCompletion>;

/**
 * A media file (with its same-named sidecars) that has settled in an agent
 * watch folder. Small sidecars travel inline so metadata, subtitles and
 * attachments can be read centrally; the media itself stays until PRISM
 * asks the agent to move or upload it.
 */
export const WatchReport = z.object({
  watchFolderId: z.string().uuid(),
  /** The agent saw this file leave the folder and come back: it was dropped again. */
  reappeared: z.boolean().default(false),
  media: z.object({ key: z.string().min(1).max(1024), sizeBytes: z.number().int().nonnegative(), modifiedAt: z.string(), checksumSha256: z.string().regex(/^[0-9a-f]{64}$/) }),
  sidecars: z.array(z.object({
    key: z.string().min(1).max(1024), sizeBytes: z.number().int().nonnegative(), checksumSha256: z.string().regex(/^[0-9a-f]{64}$/),
    /** Base64 content; omitted when larger than the inline limit. */
    contentBase64: z.string().optional(),
  })).max(50).default([]),
});
export type WatchReport = z.infer<typeof WatchReport>;
/** Sidecars up to this size are sent inline with a watch report. */
export const WATCH_SIDECAR_INLINE_BYTES = 8 * 1024 * 1024;

/** File extensions PRISM ingests, by upload or agent discovery. */
export const MEDIA_EXTENSIONS = new Set([
  'mp4', 'mov', 'm4v', 'mxf', 'mkv', 'webm', 'avi', 'mts', 'm2ts', 'ts', 'mpg', 'mpeg', 'wmv', 'flv', '3gp', 'dv', 'y4m',
  'wav', 'mp3', 'aac', 'm4a', 'flac', 'aif', 'aiff', 'ogg', 'opus', 'bwf',
  'jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'bmp', 'dpx', 'exr',
]);
export const AGENT_MEDIA_EXTENSIONS = MEDIA_EXTENSIONS;
/** HTML5 graphics packages: a .zip with manifest.json, the entry HTML, fonts and images (0.27.0). */
export const GRAPHIC_EXTENSIONS = new Set(['zip']);
/**
 * What uploads and watch folders ingest: media and graphics packages. Agent roots catalogued in place
 * (AGENT_MEDIA_EXTENSIONS) never pick up archives.
 */
export const INGEST_EXTENSIONS = new Set([...MEDIA_EXTENSIONS, ...GRAPHIC_EXTENSIONS]);
export const isGraphicPackage = (filename: string) => GRAPHIC_EXTENSIONS.has(filename.split('.').pop()?.toLowerCase() ?? '');
/** The largest graphics package accepted (the zip itself). */
export const GRAPHIC_PACKAGE_MAX_BYTES = 100 * 1024 * 1024;
